import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  FiPlus, 
  FiBook, 
  FiMenu,
  FiMapPin
} from 'react-icons/fi';
import MyNotesSidebar from '../../components/notes/MyNotesSidebar';
import notesApi from '../../services/notesApi';

const PageContainer = styled.div`
  --space-8: 8px;
  --space-12: 12px;
  --space-16: 16px;
  --space-24: 24px;
  --page-gutter: 12px;
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-24);
  min-height: calc(100vh - 2rem);
  padding: 0 var(--page-gutter) var(--space-24);
  background: var(--background-secondary);
  
  @media (min-width: 768px) {
    --page-gutter: 16px;
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: 320px 1fr;
    --page-gutter: 24px;
  }
  
  @media (min-width: 1280px) {
    --page-gutter: 32px;
  }
  
  @media (max-width: 1024px) {
    gap: var(--space-16);
    padding-bottom: var(--space-16);
  }
`;

const MainContent = styled.div`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 18px;
  padding: var(--space-24);
  box-shadow: 0 10px 24px rgba(28, 25, 23, 0.08);
  display: flex;
  flex-direction: column;
  min-height: 400px;
  min-width: 0;
  
  @media (max-width: 1024px) {
    border-radius: 0;
    border-left: none;
    border-right: none;
    border-top: none;
    min-height: calc(100vh - 80px);
    box-shadow: none;
  }
  
  @media (max-width: 640px) {
    padding: var(--space-16);
    min-height: calc(100vh - 80px);
  }
  
  @media (max-width: 480px) {
    padding: var(--space-12);
    min-height: calc(100vh - 80px);
  }
`;


const NewNoteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 22px;
  background: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
  white-space: nowrap;
  margin: 0 auto;
  min-height: 44px;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 12px 24px rgba(120, 53, 15, 0.18);
    background: var(--primary-dark);
  }
  
  @media (max-width: 640px) {
    padding: 14px 20px;
    font-size: 15px;
    gap: 10px;
    width: 100%;
    justify-content: center;
  }
  
  @media (max-width: 480px) {
    padding: 12px 16px;
    font-size: 14px;
    gap: 8px;
  }

  @media (max-width: 640px) {
    width: 100%;
    justify-content: center;
  }
`;


const MobileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 1rem;
  background: var(--background-primary);
  border-bottom: 1px solid var(--border-color);
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 8px 20px rgba(28, 25, 23, 0.06);
  
  @media (min-width: 768px) {
    padding: 16px 1.5rem;
  }
  
  @media (min-width: 1024px) {
    display: none;
  }
  
  @media (min-width: 1280px) {
    padding: 16px 2.5rem;
  }
`;

const MobileTitle = styled.h1`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const MobileMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  color: var(--text-secondary);
  cursor: pointer;
  
  &:hover {
    background: var(--background-tertiary);
  }
`;

const NotesList = styled.div`
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 18px;
  padding: var(--space-24);
  box-shadow: 0 12px 28px rgba(28, 25, 23, 0.08);
  min-width: 0;
  
  @media (max-width: 1024px) {
    padding: 20px;
  }
  
  @media (max-width: 640px) {
    padding: var(--space-16);
    border-radius: 14px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 24px;
  border: 1px dashed var(--border-color);
  border-radius: 16px;
  background: var(--background-secondary);
  color: var(--text-muted);
`;

const EmptyIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
  color: var(--text-muted);
`;

const EmptyTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 6px 0;
`;

const EmptyDescription = styled.p`
  font-size: 14px;
  color: var(--text-muted);
  margin: 0 0 16px 0;
`;

const NotesListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-16);
  padding-bottom: var(--space-16);
  border-bottom: 1px solid var(--border-color);
  
  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
    gap: var(--space-12);
  }
`;

const NotesListTitle = styled.h2`
  font-size: 1.4rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  letter-spacing: -0.01em;
  
  @media (max-width: 640px) {
    font-size: 1.25rem;
  }
`;

const NotesGrid = styled.div`
  display: grid;
  gap: var(--space-16);
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const NoteCard = styled(motion.div)`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: var(--space-16);
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 8px 18px rgba(28, 25, 23, 0.08);
  position: relative;
  overflow: hidden;
  
  &:hover {
    border-color: rgba(120, 53, 15, 0.35);
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(28, 25, 23, 0.12);
  }
  
  @media (max-width: 640px) {
    padding: 14px;
  }
`;

const NoteTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const NoteMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-8);
  font-size: 12px;
  color: var(--text-muted);
  margin-bottom: var(--space-8);
  min-width: 0;
`;

const CourseCode = styled.span`
  background: var(--background-secondary);
  color: var(--text-secondary);
  padding: 3px 8px;
  border-radius: 999px;
  font-weight: 600;
`;

const WeekBadge = styled.span`
  background: rgba(120, 53, 15, 0.08);
  color: var(--text-secondary);
  padding: 3px 8px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 11px;
`;

const TimeAgo = styled.span`
  color: var(--text-muted);
`;

const NotePreview = styled.p`
  font-size: 14px;
  color: var(--text-muted);
  margin: 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export default function NotesHome() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleNewNote = () => {
    navigate('/student/notes/new');
  };

  const handleNoteClick = (note) => {
    navigate(`/student/notes/${note.id}`);
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const getPlainTextPreview = (contentJson) => {
    if (!contentJson) return '';
    try {
      const content = typeof contentJson === 'string' ? JSON.parse(contentJson) : contentJson;
      if (content.content && Array.isArray(content.content)) {
        return content.content
          .map(block => {
            if (block.content && Array.isArray(block.content)) {
              return block.content
                .map(item => item.text || '')
                .join('');
            }
            return '';
          })
          .join(' ')
          .substring(0, 100);
      }
      return '';
    } catch (e) {
      return '';
    }
  };

  useEffect(() => {
    const fetchNotes = async () => {
      try {
        setLoading(true);
        const response = await notesApi.getMyNotes({ pageSize: 50 });
        setNotes(response.items || []);
      } catch (error) {
        console.error('Failed to fetch notes:', error);
        setNotes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNotes();
  }, []);


  return (
    <>
      <MobileHeader>
        <MobileTitle>My Notes</MobileTitle>
        <MobileMenuButton onClick={() => setSidebarOpen(true)}>
          <FiMenu size={20} />
        </MobileMenuButton>
      </MobileHeader>

      <PageContainer>
        <MyNotesSidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
        
        <MainContent>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              Loading notes...
            </div>
          ) : (
            <NotesList>
              <NotesListHeader>
                <NotesListTitle>Your Notes</NotesListTitle>
                <NewNoteButton onClick={handleNewNote}>
                  <FiPlus size={16} />
                  New Note
                </NewNoteButton>
              </NotesListHeader>

              {notes.length > 0 ? (
                <NotesGrid>
                  {notes.map((note, index) => (
                    <NoteCard
                      key={note.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0 }}
                      onClick={() => handleNoteClick(note)}
                    >
                      <NoteTitle>
                        {Boolean(note.is_pinned) && <FiMapPin size={14} color="var(--primary-color)" />}
                        {note.title || 'Untitled note'}
                      </NoteTitle>
                      <NoteMeta>
                        <CourseCode>{note.course_code || 'Other'}</CourseCode>
                        <WeekBadge>{note.week_number ? `W${note.week_number}` : 'No week'}</WeekBadge>
                        <TimeAgo>{formatTimeAgo(note.updated_at)}</TimeAgo>
                      </NoteMeta>
                      <NotePreview>
                        {getPlainTextPreview(note.content_json)}
                      </NotePreview>
                    </NoteCard>
                  ))}
                </NotesGrid>
              ) : (
                <EmptyState>
                  <EmptyIcon>
                    <FiBook />
                  </EmptyIcon>
                  <EmptyTitle>No notes yet</EmptyTitle>
                  <EmptyDescription>
                    Create your first note to keep track of key ideas and reflections.
                  </EmptyDescription>
                  <NewNoteButton onClick={handleNewNote}>
                    <FiPlus size={16} />
                    Create Your First Note
                  </NewNoteButton>
                </EmptyState>
              )}
            </NotesList>
          )}
        </MainContent>
      </PageContainer>
    </>
  );
}
